// components/cart/PaymentSelector.js
import React, { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useDispatch, useSelector } from 'react-redux';
import { setComisionMP, setCupon } from 'store/carritoSlice';
import { toast } from 'react-toastify';
import { validarCupon } from 'scripts/validarCupon';
import {
	getViandaCouponBlockReason,
	getViandaCouponUniversalBlockReason,
} from 'utils/viandaCouponRules';

const Container = tw.div`flex flex-col w-full`;
const Title = tw.h2`text-2xl font-bold mb-4`;
const PaymentOption = tw.div`flex items-center mb-4 cursor-pointer p-3 rounded-lg border border-gray-300 hover:bg-gray-100`;
const CuponContainer = tw.div`flex w-full items-center mb-4`;
const Cupon = tw.input`w-2/3 sm:flex-1 min-w-0 mb-4 cursor-pointer p-3 rounded-lg border border-gray-300 hover:bg-gray-100`;
const ValidateButton = tw.button`ml-2 mb-4 shrink-0 px-4 py-2 bg-[#99cdb6] text-white rounded hover:bg-primary-800`;
const Label = tw.label`ml-2 text-gray-700`;
const SelectButton = tw.button`mt-4 px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-800 self-start`;
const WarningText = tw.p`text-sm text-yellow-700 bg-yellow-100 border border-yellow-300 rounded p-3 mb-4`;

const PaymentSelector = ({
	setStep,
	metodoPago,
	setMetodoPago,
	handleMercadoPago,
	setPreferenceId,
	deliveryMethod,
	soloRecetarios,
	backStep = 3,
}) => {
	const dispatch = useDispatch();
	const [codigo, setCodigo] = useState('');
	const user = useSelector((state) => state.usuario.userInfo);
	const empresa = useSelector((state) => state.empresa || {});
	const cartItems = useSelector((state) => state.carrito.items || []);
	const cuponAplicado = useSelector((state) => state.carrito.cupon);
	const [couponValidationMessage, setCouponValidationMessage] = useState('');
	const motivoBloqueoUniversal = getViandaCouponUniversalBlockReason(cartItems);
	const motivoBloqueoCuponAplicado = cuponAplicado ?
		getViandaCouponBlockReason(cartItems, cuponAplicado.alcance)
	:	null;
	const motivoBloqueoCupon = motivoBloqueoUniversal || motivoBloqueoCuponAplicado || couponValidationMessage;
	const cuponBloqueado = Boolean(motivoBloqueoUniversal);
	const mercadoPagoHabilitado = empresa?.mercado_pago_habilitado !== false;
	const puedeUsarMercadoPago = user?.rol_id !== 1 && mercadoPagoHabilitado;

	const handleCuponChange = (e) => {
		setCouponValidationMessage('');
		setCodigo(e.target.value.toUpperCase());
	};

	const handleValidateCupon = () => {
		if (motivoBloqueoUniversal) {
			toast.info(motivoBloqueoUniversal);
			dispatch(setCupon(''));
			return;
		}

		validarCupon(codigo, sessionStorage.getItem('token'))
			.then((data) => {
				const motivoParaCupon = getViandaCouponBlockReason(cartItems, data.alcance);
				if (motivoParaCupon) {
					setCouponValidationMessage(motivoParaCupon);
					toast.info(motivoParaCupon);
					dispatch(setCupon(''));
					return;
				}
				setCouponValidationMessage('');
				toast.success('Cupón validado: ' + data.codigo);
				dispatch(setCupon(data));
			})
			.catch((e) => {
				setCouponValidationMessage('');
				toast.error(e.message);
				dispatch(setCupon(''));
			});
	};

	useEffect(() => {
		setCouponValidationMessage('');
	}, [cartItems, cuponAplicado]);

	useEffect(() => {
		if (metodoPago === 'mercado-pago') {
			dispatch(setComisionMP(5.99));
			handleMercadoPago();
		} else {
			dispatch(setComisionMP(0));
			setPreferenceId(null);
		}
	}, [metodoPago, dispatch, handleMercadoPago, setPreferenceId]);

	useEffect(() => {
		if (!puedeUsarMercadoPago && metodoPago === 'mercado-pago') {
			setMetodoPago('transferencia');
			dispatch(setComisionMP(0));
			setPreferenceId(null);
		}
	}, [puedeUsarMercadoPago, metodoPago, setMetodoPago, dispatch, setPreferenceId]);

	useEffect(() => {
		if (motivoBloqueoCuponAplicado) {
			dispatch(setCupon(''));
		}
	}, [motivoBloqueoCuponAplicado, dispatch]);

	return (
		<Container>
			<Title>Selecciona un método de pago</Title>
			{puedeUsarMercadoPago && (
				<PaymentOption onClick={() => setMetodoPago('mercado-pago')}>
					<input type="radio" name="metodo-pago" checked={metodoPago === 'mercado-pago'} readOnly />
					<Label>Pagar con Mercado Pago (+5,99% de comisión)</Label>
				</PaymentOption>
			)}
			<PaymentOption onClick={() => setMetodoPago('transferencia')}>
				<input type="radio" name="metodo-pago" checked={metodoPago === 'transferencia'} readOnly />
				<Label>Pagar por Transferencia</Label>
			</PaymentOption>
			{deliveryMethod === 'pickup' && !soloRecetarios && (
				<PaymentOption onClick={() => setMetodoPago('efectivo')}>
					<input type="radio" name="metodo-pago" checked={metodoPago === 'efectivo'} readOnly />
					<Label>Pago con efectivo o POS (solo al retirar)</Label>
				</PaymentOption>
			)}
			<Title>Cupón de descuento</Title>
			{motivoBloqueoCupon && <WarningText>{motivoBloqueoCupon}</WarningText>}
			<CuponContainer>
				<Cupon value={codigo} onChange={(e) => handleCuponChange(e)} disabled={cuponBloqueado} />
				<ValidateButton onClick={handleValidateCupon} disabled={cuponBloqueado}>
					Validar
				</ValidateButton>
			</CuponContainer>
			<SelectButton onClick={() => setStep(backStep)}>Volver</SelectButton>
		</Container>
	);
};

export default PaymentSelector;
